import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';

const Hero = () => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const backgroundSlides = [
    // Existing images
    { type: 'image', url: 'https://customer-assets.emergentagent.com/job_design-crafters-2/artifacts/885fctx6_624623072_781554964987700_1951070273906982112_n.jpg' },
    { type: 'image', url: 'https://customer-assets.emergentagent.com/job_design-crafters-2/artifacts/n0t9e2dl_625022381_2742323672770201_4074171484459488215_n.jpg' },
    { type: 'image', url: 'https://customer-assets.emergentagent.com/job_design-crafters-2/artifacts/zmrwuj86_610542574_1810568502990387_276701751795073548_n%20%281%29.jpg' },
    // NEW: 2 uploaded images (removed 4th photo)
    { type: 'image', url: 'https://customer-assets-4nw71qhi.emergentagent.net/job_54d45426-6e46-45aa-b51c-b65e128b9e72/artifacts/0u70yk7k_WhatsApp%20Image%202026-07-17%20at%208.48.53%20AM.jpeg' },
    { type: 'image', url: 'https://customer-assets-4nw71qhi.emergentagent.net/job_54d45426-6e46-45aa-b51c-b65e128b9e72/artifacts/384jv6gi_WhatsApp%20Image%202026-06-25%20at%209.57.24%20AM.jpeg' },
    // NEW: Replaced video
    { type: 'video', url: 'https://customer-assets-4nw71qhi.emergentagent.net/job_54d45426-6e46-45aa-b51c-b65e128b9e72/artifacts/qamje2ze_replace.mp4' },
    { type: 'image', url: 'https://customer-assets.emergentagent.com/job_design-crafters-2/artifacts/9ptlqe0u_598525352_694759526902158_2178918516253509462_n.jpg' },
    { type: 'image', url: 'https://customer-assets.emergentagent.com/job_design-crafters-2/artifacts/bjgk0cq8_598664104_1398208162047985_7612617029880670438_n.jpg' },
    { type: 'image', url: 'https://customer-assets.emergentagent.com/job_design-crafters-2/artifacts/2di8jvem_624585733_1959604521622983_5321104814325544422_n.jpg' },
    { type: 'image', url: 'https://customer-assets.emergentagent.com/job_design-crafters-2/artifacts/6a5a9u8v_624467700_1973615519892419_1561903938972216275_n.jpg' }
  ];

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setCurrentSlideIndex((prevIndex) => 
        (prevIndex + 1) % backgroundSlides.length
      );
    }, 6000);

    return () => clearInterval(interval);
  }, [backgroundSlides.length]);

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Full-screen cinematic background with images and video */}
      {backgroundSlides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-2000 ${
            index === currentSlideIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {slide.type === 'image' ? (
            <div
              style={{
                backgroundImage: `url(${slide.url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                animation: index === currentSlideIndex ? 'kenBurns 12s ease-out' : 'none',
                width: '100%',
                height: '100%'
              }}
            />
          ) : (
            <video
              key={`video-${index}`}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
              style={{
                animation: index === currentSlideIndex ? 'kenBurns 12s ease-out' : 'none'
              }}
            >
              <source src={slide.url} type="video/mp4" />
            </video>
          )}
        </div>
      ))}

      {/* Sophisticated warm overlay - less heavy, more natural */}
      <div className="absolute inset-0 bg-gradient-to-br from-navy/60 via-burgundy/50 to-navy/70"></div>
      
      {/* Subtle vignette */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(circle at center, transparent 20%, rgba(14, 31, 53, 0.4) 100%)'
      }}></div>

      {/* Minimal elegant content */}
      <div className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1500 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}>
        <div className="space-y-8 sm:space-y-10">
          {/* Main headline - elegant and refined */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-playfair font-light text-warm-white tracking-tight leading-tight drop-shadow-2xl px-4">
            Where Dimension<br />
            <span className="font-medium">Takes Form.</span>
          </h1>
          
          {/* Subtle divider */}
          <div className="h-px w-24 sm:w-32 bg-gold mx-auto opacity-70"></div>
          
          {/* Small elegant subtext */}
          <p className="text-sm sm:text-base lg:text-lg xl:text-xl text-warm-white/90 font-manrope font-light tracking-wider max-w-2xl mx-auto uppercase px-4">
            Premium Cabinetry • Interior Solutions • Customizable Designs
          </p>

          {/* CTA Buttons with luxury styling */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-12">
            <button
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="group bg-gold text-navy px-8 py-4 rounded-lg text-base font-manrope font-semibold hover:bg-gold-light transition-all duration-300 shadow-lg hover:shadow-xl hover:translate-y-[-2px] flex items-center gap-2"
            >
              <span>Book Consultation</span>
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <button
              onClick={() => {
                const element = document.getElementById('gallery');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="group bg-transparent text-warm-white border border-warm-white/50 px-8 py-4 rounded-lg text-base font-manrope font-medium hover:bg-warm-white/10 hover:border-warm-white transition-all duration-300 backdrop-blur-sm"
            >
              View Portfolio
            </button>
          </div>
        </div>
      </div>



      <style jsx>{`
        @keyframes kenBurns {
          0% {
            transform: scale(1);
          }
          100% {
            transform: scale(1.08);
          }
        }
      `}</style>
    </section>
  );
};

export default Hero;
